/*
 * <first-order-discount> — mirrors the announcement bar's first-order coupon CTA
 * next to the product price (mobile). It reuses the announcement bar's coupon link
 * and its apply logic, so the coupon code and the apply behaviour live in one place.
 *
 * States:
 *  - default: shows the "Get your 10% discount" button + disclaimer (translated via locales).
 *  - applied: after the coupon is applied (or on reload when it already is), shows the
 *    "Welcome to Olivida!" success button + confirmation, and reveals the discounted
 *    price with the original struck through.
 * Only active when a coupon is configured.
 */
const FIRST_ORDER_APPLIED_COLOR = '#2fa66e';

class FirstOrderDiscount extends HTMLElement {
  connectedCallback() {
    // All listeners are registered with this signal so a section re-render
    // (disconnect + reconnect) never leaves stale handlers behind.
    this.abortController = new AbortController();
    this.init();
  }

  async init() {
    this.link = this.querySelector('a');
    if (!this.link) return;

    // The announcement bar component owns the coupon code, the apply logic and
    // the cookie state this button mirrors — wait until it is defined instead
    // of depending on script load order.
    await customElements.whenDefined('o-announcement-bar');
    if (this.abortController.signal.aborted) return;

    const bar = document.querySelector('o-announcement-bar');
    this.barLink = bar && bar.querySelector('a[data-coupon-code]');
    if (!this.barLink) return;

    this.bar = bar;
    // Scope the applied state to the whole product info block so both the main price
    // and the variant list react to it.
    this.appliedScope = this.closest('.product__info-container') || this.closest('.product__price');
    this.variantId = this.dataset.variantId;
    this.moneyFormat = this.dataset.moneyFormat;

    this.link.href = this.barLink.getAttribute('href');
    this.link.setAttribute('data-coupon-code', this.barLink.dataset.couponCode || '');

    // Match the button to the announcement bar's colours; the CSS owns the fallback teal.
    const barStyles = getComputedStyle(bar);
    const transparent = ['rgba(0, 0, 0, 0)', 'transparent', ''];
    if (transparent.indexOf(barStyles.backgroundColor) === -1) {
      this.link.style.backgroundColor = barStyles.backgroundColor;
    }
    if (transparent.indexOf(barStyles.color) === -1) {
      this.link.style.color = barStyles.color;
    }

    // Snapshot the default presentation so resetToDefault() can revert showApplied().
    this.defaultBackgroundColor = this.link.style.backgroundColor;
    this.defaultColor = this.link.style.color;
    this.defaultAriaLabel = this.link.getAttribute('aria-label');

    this.classList.add('is-ready');

    // Re-sync with Shopify's real price whenever the cart changes (qty edits, add/remove)
    // — but only once the discount is applied (otherwise the estimate stays shown).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (this.classList.contains('is-applied')) this.syncCartPrice();
      });
    }

    const { signal } = this.abortController;
    this.link.addEventListener('click', this.handleClick.bind(this), { signal });
    // Stay in sync with the announcement bar: reflect its loading / success / failure
    // states here too, whether the apply was triggered from the bar or this button.
    document.addEventListener('first-order-coupon:applying', () => this.showApplying(), { signal });
    document.addEventListener(
      'first-order-coupon:applied',
      (event) => this.showApplied(true, event.detail?.cart),
      { signal }
    );
    document.addEventListener('first-order-coupon:failed', () => this.resetApplying(), { signal });
    // Silent transitions from the bar's cart reconciliation: applied state restored
    // from the cart (no confetti), or the stored state turned out to be stale.
    document.addEventListener(
      'first-order-coupon:restored',
      (event) => this.showApplied(false, event.detail?.cart),
      { signal }
    );
    document.addEventListener('first-order-coupon:reset', () => this.resetToDefault(), { signal });

    if (bar.isCouponClaimed?.()) {
      // Discount already active (e.g. applied on an earlier visit) — show the success state.
      this.showApplied();
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  handleClick(event) {
    event.preventDefault();
    if (this.classList.contains('is-applying') || this.classList.contains('is-applied')) return;

    // Instant loading feedback, then delegate to the announcement bar's handler (applies
    // the coupon, sets the cookie, updates the cart drawer, fires analytics + events;
    // under `shopify theme dev` it fakes the apply, so no special-casing here).
    this.showApplying();
    this.barLink.click();
  }

  showApplying() {
    if (this.classList.contains('is-applied')) return;
    this.classList.add('is-applying');
  }

  resetApplying() {
    this.classList.remove('is-applying');
  }

  showApplied(celebrate, cart) {
    if (this.classList.contains('is-applied')) return;
    this.classList.remove('is-applying');
    this.classList.add('is-applied');
    this.link.style.backgroundColor = FIRST_ORDER_APPLIED_COLOR;
    this.link.style.color = '#fff';
    this.link.removeAttribute('href');
    this.link.setAttribute('aria-label', this.dataset.appliedAriaLabel || 'Welcome to Olivida! Your discount is active.');
    if (this.appliedScope) {
      this.appliedScope.classList.add('is-first-order-applied');
    }
    // Prefer Shopify's real price when the product is already in the cart.
    this.syncCartPrice(cart);
    // Only celebrate on a fresh apply — not when restoring the applied state on reload.
    if (celebrate) {
      this.celebrate();
    }
  }

  // Revert showApplied(): the announcement bar found the stored applied state to be
  // stale and unrecoverable, so offer the coupon again.
  resetToDefault() {
    this.classList.remove('is-applying', 'is-applied');
    this.link.style.backgroundColor = this.defaultBackgroundColor;
    this.link.style.color = this.defaultColor;
    this.link.setAttribute('href', this.barLink.getAttribute('href'));
    this.link.setAttribute('aria-label', this.defaultAriaLabel);
    if (this.appliedScope) {
      this.appliedScope.classList.remove('is-first-order-applied');
    }
  }

  // When the product is already a cart line, replace the computed estimate with
  // Shopify's actual discounted price — exact parity, including order-level
  // rounding. Accepts an already-fetched cart (e.g. from the restore event) so
  // the same /cart.js isn't fetched twice in one page load.
  syncCartPrice(cart) {
    if (!this.variantId) return;

    if (cart) {
      this.showCartLinePrice(cart);
      return;
    }

    if (typeof routes === 'undefined' || !routes || !routes.cart_url) return;

    fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((fetched) => this.showCartLinePrice(fetched))
      .catch(() => {});
  }

  showCartLinePrice(cart) {
    if (!cart || !cart.items) return;
    const line = cart.items.find((item) => String(item.variant_id) === String(this.variantId));
    if (!line) return;

    // Only take over the estimate once the cart line is genuinely cheaper. If the
    // coupon never landed (apply failed, code removed, or it sits at cart level
    // instead of on the line), final_price is the undiscounted price — writing that
    // into the discounted slot makes the discount look like it vanished the moment
    // the product enters the cart, while the button still says it is active.
    if (line.final_price >= line.price) return;

    const formatted = this.formatPrice(line.final_price);
    // Every welcome-price slot (main price, variant rows, plan picker) carries
    // data-first-order-price="<variant id>". Only this variant's slots take the
    // cart price; the other variant rows keep their own Liquid estimate.
    this.appliedScope.querySelectorAll(`[data-first-order-price="${this.variantId}"]`).forEach((el) => {
      el.innerHTML = formatted;
    });
  }

  // Mirrors the theme's money display: uses shop.money_format and drops the currency
  // symbol at >= €100 (same rule as snippets/money-amount.liquid).
  formatPrice(cents) {
    const format = this.moneyFormat || '{{amount}}';
    const placeholder = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    const thousands = placeholder.includes('comma_separator')
      ? '.'
      : placeholder.includes('space_separator')
        ? ' '
        : ',';
    const decimal = placeholder.includes('comma_separator') ? ',' : '.';
    const precision = placeholder.includes('no_decimals') ? 0 : 2;

    const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const number = fraction ? grouped + decimal + fraction : grouped;

    if (cents >= 10000) return number;
    return format.replace(/\{\{\s*\w+\s*\}\}/, number);
  }

  celebrate() {
    // canvas-confetti (vendored in assets/confetti.browser.min.js — see scripts/vendor-assets.mjs).
    if (typeof confetti !== 'function') return;

    const rect = this.link.getBoundingClientRect();
    const origin = {
      x: (rect.left + rect.width / 2) / window.innerWidth,
      y: (rect.top + rect.height / 2) / window.innerHeight,
    };

    const heart =
      typeof confetti.shapeFromPath === 'function'
        ? confetti.shapeFromPath({
            path: 'M12 21s-6.716-4.297-9.428-7.01C.86 12.276.86 9.724 2.572 8.01a4.5 4.5 0 0 1 6.364 0L12 11.07l3.064-3.06a4.5 4.5 0 0 1 6.364 6.364C18.716 16.703 12 21 12 21z',
          })
        : null;

    confetti({
      particleCount: 130,
      spread: 75,
      startVelocity: 45,
      origin: origin,
      scalar: 1.6,
      // Olivida light-blue palette — saturated tints around the brand teal #6c98aa
      colors: ['#4f7c8e', '#6c98aa', '#8fb4c2', '#a9d3e2', '#bfe4f2'],
      shapes: heart ? [heart] : undefined,
    });
  }
}

customElements.define('first-order-discount', FirstOrderDiscount);
